import React, { useState } from "react";
import { getValidPriceLevels, PRICE_LEVELS } from "../priceLevels.js";

/**
 * All API request functions should have a parameter signal if using abortController
 * useAbortController allows to abort concurrent "old" requests.
 */
const useAlgolia = () => {
  const endPointURL =
    window.location.origin + "/wp-json/hoas-algolia/v1/algolia-search";
  const METHOD = "POST";

  const getData = (params, signal) => {
    const algoliaParams = costUnitParams(params);

    let final = {
      costUnits: [],
      count: 0,
    };

    return fetch(endPointURL, {
      method: METHOD,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(algoliaParams),
      ...(signal ? { signal } : {}),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            "Error: fetching data from ALGOLIA: " + response.status,
          );
        }
        return response.json();
      })
      .then((data) => {
        final.costUnits = data.hits;
        final.count = data.nbHits;
        return final;
      })
      .catch((error) => {
        throw error;
      });
  };

  const getFreeApartments = async (params, signal) => {
    const algoliaParams = apartmentParams(params);

    return fetch(endPointURL, {
      method: METHOD,
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(algoliaParams),
      ...(signal ? { signal } : {}),
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(
            "Error: fetching data from ALGOLIA: " + response.status,
          );
        }
        return response.json();
      })
      .then((data) => {
        return data?.hits ?? [];
      })
      .catch((error) => {
        throw error;
      });
  };

  const costUnitParams = (params = {}) => {
    const locations = params.locations;
    const rentalTypes = params.rentalTypes;
    const extraInfos = params.extraInfos;
    const page = params.page ? params.page - 1 : 0; //Url has params for example: page=1 --> get page 0
    const hitsPerPage = params.hitsPerPage;
    const rent = params.rent;
    const exchangeStudents = params.exchangeStudents;
    const surfaceArea = params.surfaceArea;
    const priceLevels = params.priceLevels ?? [];
    let facetFilters = [];
    let filters = [];

    if (Array.isArray(locations) && locations.length > 0) {
      // Iterate over locations and build the query
      let arr = [];

      locations.forEach((location) => {
        arr.push(
          `city:${location}`,
          `quarter_of_town:${location}`,
          `address:${location}`,
        );
      });
      facetFilters.push(arr);
    }

    //Extra infos filtering
    if (extraInfos.includes("elevator")) {
      facetFilters.push(`elevator: ${true}`);
    }
    if (extraInfos.includes("laundry_room")) {
      facetFilters.push(`laundry_room: ${true}`);
    }
    if (extraInfos.includes("renovated_or_new")) {
      facetFilters.push(`renovated_or_new: ${true}`);
    }
    if (extraInfos.includes("favorite")) {
      facetFilters.push(`favorite: "${1}"`);
    }

    let numericFilters = [];
    if (rent) {
      const rentFilter = handleRent(rentalTypes, rent);
      if (rentFilter?.length > 0) {
        numericFilters.push(rentFilter);
      }
    }

    if (surfaceArea) {
      const surfaceAreaFilter = handleSurfaceArea(rentalTypes, surfaceArea);

      if (surfaceAreaFilter?.length > 0) {
        numericFilters.push(surfaceAreaFilter);
      }
    }

    if (exchangeStudents) {
      facetFilters.push("student_exchange: 1");
    }

    const priceLevelFilter = handlePriceLevels(priceLevels);
    if (priceLevelFilter) {
      filters.push(priceLevelFilter);
    }

    //Get only cost-units
    facetFilters.push("type: Kustannuspaikka");

    //Apartment_Category filtering
    let apartment_categories = [];
    if (rentalTypes.includes("Erityisryhmät")) {
      apartment_categories.push(`apartment_categories: Erityisryhmät`);
    }

    if (rentalTypes.includes("Furnished room")) {
      apartment_categories.push(`apartment_categories: Furnished room`);
    }

    if (rentalTypes.includes("Furnished shared studio")) {
      apartment_categories.push(
        `apartment_categories: Furnished shared studio`,
      );
    }

    if (rentalTypes.includes("Kaksio")) {
      apartment_categories.push(`apartment_categories: Kaksio`);
      apartment_categories.push(`apartment_categories: Kaksio/2 rooms`);
    }

    if (rentalTypes.includes("Kolmio+/3 rooms+")) {
      apartment_categories.push(`apartment_categories: Kolmio+/3 rooms+`);
    }

    if (rentalTypes.includes("Solu/Room")) {
      apartment_categories.push(`apartment_categories: Solu/Room`);
    }

    if (rentalTypes.includes("Yksiö/Studio")) {
      apartment_categories.push(`apartment_categories: Yksiö/Studio`);
    }

    if (
      Array.isArray(apartment_categories) &&
      apartment_categories.length > 0
    ) {
      facetFilters.push(apartment_categories);
    }

    const object = {
      facetFilters: facetFilters,
      page: page,
      hitsPerPage: hitsPerPage,
    };

    if (numericFilters.length > 0) {
      object.numericFilters = numericFilters;
    }

    if (filters.length > 0) {
      object.filters = filters.join(" AND ");
    }

    return object;
  };

  const apartmentParams = (params = {}) => {
    const locations = params.locations;
    const rentalTypes = params.rentalTypes;
    const rent = params.rent;
    const surfaceArea = params.surfaceArea;
    const exchangeStudents = params.exchangeStudents;
    const priceLevels = params.priceLevels ?? [];

    let facetFilters = [];
    let filters = [];
    if (Array.isArray(locations) && locations.length > 0) {
      let arr = [];
      // Iterate over locations and build the query
      locations.forEach((location) => {
        arr.push(
          `city:${location}`,
          `quarter_of_town:${location}`,
          `address:${location}`,
        );
      });
      facetFilters.push(arr);
    }

    //Apartment_Category filtering
    let apartment_category = [];
    if (rentalTypes.includes("Erityisryhmät")) {
      apartment_category.push(`apartment_category: Erityisryhmät`);
    }
    if (rentalTypes.includes("Furnished room")) {
      apartment_category.push(`apartment_category: Furnished room`);
    }
    if (rentalTypes.includes("Furnished shared studio")) {
      apartment_category.push(`apartment_category: Furnished shared studio`);
    }
    if (rentalTypes.includes("Kaksio")) {
      apartment_category.push(`apartment_category: Kaksio`);
      apartment_category.push(`apartment_category: Kaksio/2 rooms`);
    }
    if (rentalTypes.includes("Kolmio+/3 rooms+")) {
      apartment_category.push(`apartment_category: Kolmio+/3 rooms+`);
    }
    if (rentalTypes.includes("Solu/Room")) {
      apartment_category.push(`apartment_category: Solu/Room`);
    }
    if (rentalTypes.includes("Yksiö/Studio")) {
      apartment_category.push(`apartment_category: Yksiö/Studio`);
    }

    if (Array.isArray(apartment_category) && apartment_category.length > 0) {
      facetFilters.push(apartment_category);
    }

    let numericFilters = [];

    if (rent) {
      numericFilters.push(`rent <= ${rent}`);
    }

    if (surfaceArea) {
      numericFilters.push(`surface_area >= ${surfaceArea}`);
    }

    if (exchangeStudents) {
      facetFilters.push("student_exchange: 1");
    }

    const priceLevelFilter = handlePriceLevels(priceLevels);
    if (priceLevelFilter) {
      filters.push(priceLevelFilter);
    }

    //Get only apartments
    const roomsAndApartments = ["type: Huoneisto", "type: Huone"];
    facetFilters.push(roomsAndApartments);

    const object = {
      facetFilters: facetFilters,
    };

    if (numericFilters.length > 0) {
      object.numericFilters = numericFilters;
    }

    if (filters.length > 0) {
      object.filters = filters.join(" AND ");
    }

    return object;
  };

  const handleRent = (rentalTypes, rent) => {
    if (rentalTypes?.length <= 0) {
      return [`rents.costunit.min_rent <= ${rent}`];
    }
    //Has params value for spesific apartment_category and for rent
    let rentalTypesOr = [];
    rentalTypes.forEach((r) => {
      const regex = /[^a-zA-Z]/gm;
      const str = r;
      const result = str.replace(regex, "").toLowerCase();

      if (r === "Kaksio") {
        const secondString = "Kaksio/2 rooms";
        const secondRgxed = secondString.replace(regex, "").toLowerCase();
        rentalTypesOr.push(`rents.${result}.min_rent <= ${rent}`);
        rentalTypesOr.push(`rents.${secondRgxed}.min_rent <= ${rent}`);
        return;
      }
      rentalTypesOr.push(`rents.${result}.min_rent <= ${rent}`);
    });
    return rentalTypesOr;
  };

  const handleSurfaceArea = (rentalTypes, surfaceArea) => {
    if (rentalTypes?.length <= 0) {
      return [`surface_areas.costunit.max_surface_area >= ${surfaceArea}`];
    }

    //Has params value for spesific apartment_category and for surfaceArea
    let rentalTypesOr = [];
    rentalTypes.forEach((r) => {
      const regex = /[^a-zA-Z]/gm;
      const str = r;
      const result = str.replace(regex, "").toLowerCase();

      if (r === "Kaksio") {
        //Check for kaksios
        const secondString = "Kaksio/2 rooms";
        const secondRgxed = secondString.replace(regex, "").toLowerCase();
        rentalTypesOr.push(
          `surface_areas.${result}.max_surface_area >= ${surfaceArea}`,
        );
        rentalTypesOr.push(
          `surface_areas.${secondRgxed}.max_surface_area >= ${surfaceArea}`,
        );
      } else {
        //check by exactly the selected rental type
        rentalTypesOr.push(
          `surface_areas.${result}.max_surface_area >= ${surfaceArea}`,
        );
      }
    });

    return rentalTypesOr;
  };

  const handlePriceLevels = (priceLevels = []) => {
    if (!Array.isArray(priceLevels) || priceLevels.length <= 0) {
      return "";
    }

    const normalizedPriceLevels = getValidPriceLevels(priceLevels);

    if (
      normalizedPriceLevels.length <= 0 ||
      normalizedPriceLevels.length === PRICE_LEVELS.length
    ) {
      return "";
    }

    if (normalizedPriceLevels.includes("Matala")) {
      return PRICE_LEVELS.filter(
        (priceLevel) => !normalizedPriceLevels.includes(priceLevel),
      )
        .map((priceLevel) => `NOT price_level:${priceLevel}`)
        .join(" AND ");
    }

    const selectedPriceLevelFilter = normalizedPriceLevels
      .map((priceLevel) => `price_level:${priceLevel}`)
      .join(" OR ");

    if (normalizedPriceLevels.length > 1) {
      return `(${selectedPriceLevelFilter})`;
    }

    return selectedPriceLevelFilter;
  };

  return {
    getData,
    getFreeApartments,
  };
};

export default useAlgolia;
